import { usePDFPrintHook } from "@careinfo/layout";
import { tw } from "@careinfo/tailwind";
import loadable from "@loadable/component";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Spinner from "../../../components/Reusable/Spinner";
import APIS from "../../../constant/Endpoint";
import useGetHook from "../../../customHooks/useGetHook";
import {
  patchApiData,
  patchApiFormData,
  postApiData,
} from "../../../helpers/AxiosInstance";
import usePostHook from "../../../customHooks/usePostHook";
const EditCashReceiptComponent = loadable(
  () =>
    import("../../../components/Transaction/Cash/CashReceipt/EditCashReceipt")
);

const EditCashReceipt = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [selectedOrder, setSelectedOrder] = useState(null);
  const { downloadPrint, isPrintLoading } = usePDFPrintHook();
  const [openPrintPopup, setOpenPrintPopup] = useState(false);
  const [isRevertLoading, setRevertLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const queryClient = useQueryClient();

  const { data, isLoading: isDataLoading } = useGetHook({
    url: `${APIS.BILL}${id}/`,
    queryKey: ["cash-receipt", `cash-receipt-${id}`],
    params: {},
  });

  const handleEdit = async (values: any, isDraft: any, setError: any) => {
    setIsLoading(true);
    const url = `${APIS.BILL}${id}/`;
    const formData = new FormData();
    formData.append("voucher_date", values?.voucher_date ?? null);
    formData.append("reference_number", values?.reference_number ?? "");
    values?.reference_date
      ? formData.append("reference_date", values?.reference_date)
      : null;
    formData.append("amount", values?.amount ?? 0);
    formData.append("account", values?.account);
    formData.append("party_account", values?.party_account);
    formData.append("number_series", values?.number_series);
    formData.append("entry_number", values?.entry_number);
    formData.append("balance_type", "debit");
    formData.append("type", "Cash Receipt");
    if (values?.attachment?.[0] && typeof values?.attachment !== "string") {
      formData.append("attachment", values?.attachment?.[0]);
    }
    formData.append("narration", values?.narration ?? "");
    isDraft
      ? formData.append("status", "Draft")
      : formData.append("status", "Success");
    try {
      const res = await patchApiFormData({ url, formData, setError });
      if (res?.status === 200) {
        queryClient.invalidateQueries({
          queryKey: ["cash-receipt"],
          refetchType: "active",
        });
        if (!isDraft) {
          setSelectedOrder(res?.data?.id);
          setOpenPrintPopup(true);
        } else {
          navigate("/accounting/cash-receipt");
        }
        toast.success("Updated Successfully");
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const { mutateAsync, isPostLoading } = usePostHook({
    queryKey: ["cash-account"],
    navigateURL: "",
  });
  const handleCreateCashAccount = async (
    values: any,
    setOpenAccountPopup: any,
    setError: any
  ) => {
    const url = APIS.COA;
    const formData = {
      ...values,
      is_group: false,
    };
    try {
      const res = await mutateAsync({ url, formData, setError });
      if (res?.status === 201) {
        setOpenAccountPopup(false);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handlePrint = async () => {
    const url = APIS.BILL_PRINT;
    let formData = {
      ids: [selectedOrder],
      type: "Cash Receipt",
    };
    try {
      const res = await downloadPrint(url, formData);
    } catch (err) {
      console.error(err);
    }
  };

  const handleCancel = async () => {
    const url = `${APIS.BILL}${id}/`;
    const formData = {
      status: "Cancelled",
    };
    try {
      patchApiData({ url, formData, setError: () => {} });
    } catch (err) {
      console.error(err);
    }
  };

  const handleRevert = async (setError) => {
    const url = `${APIS.BILL}${id}/revert/`;
    setRevertLoading(true);
    try {
      const res = await postApiData({ url, formData: {}, setError });
      if (res?.status === 200) {
        queryClient.invalidateQueries({
          queryKey: ["cash-receipt"],
          refetchType: "active",
        });
      }
    } catch (err) {
      console.error(err);
    } finally {
      setRevertLoading(false);
    }
  };

  return (
    <div>
      <div className={tw`grid grid-cols-2 `}>
        <h1 className={tw`font-semibold text-xl leading-10`}>
          Edit Cash Receipt
        </h1>
      </div>
      {isDataLoading ? (
        <Spinner />
      ) : (
        <EditCashReceiptComponent
          handleEdit={handleEdit}
          isLoading={isLoading}
          data={data}
          handleCreateCashAccount={handleCreateCashAccount}
          isPostLoading={isPostLoading}
          openPrintPopup={openPrintPopup}
          setOpenPrintPopup={setOpenPrintPopup}
          handlePrint={handlePrint}
          isPrintLoading={isPrintLoading}
          handleRevert={handleRevert}
          handleCancel={handleCancel}
          isRevertLoading={isRevertLoading}
          id={id}
        />
      )}
    </div>
  );
};

export default EditCashReceipt;
